import { createContext, useCallback, useContext } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api-client'

export interface User {
  id: number
  username: string
  email: string
  name: string
  is_admin: boolean
}

interface AuthContextValue {
  user: User | null
  isLoading: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function fetchMe(): Promise<User | null> {
  try {
    const { data } = await apiClient.get<User>('/auth/me')
    return data
  } catch {
    // 401 is the normal "not logged in" answer, not an error worth surfacing.
    return null
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient()

  // The session lives in an httpOnly cookie, so the browser holds it and JS
  // cannot read it — the only way to know who we are is to ask the server.
  const { data: user, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    retry: false,
    staleTime: 5 * 60_000,
  })

  const login = useCallback(
    async (username: string, password: string) => {
      const { data } = await apiClient.post<User>('/auth/login', { username, password })
      qc.setQueryData(['auth', 'me'], data)
      // Everything cached belonged to the previous (or no) user.
      qc.invalidateQueries()
    },
    [qc],
  )

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout')
    } finally {
      qc.setQueryData(['auth', 'me'], null)
      qc.clear()
    }
  }, [qc])

  return (
    <AuthContext.Provider value={{ user: user ?? null, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
